// Lightweight auth session backed by localStorage so the logged-in identity
// persists across refreshes.

const SESSION_KEY = "mp_demo_session";

export type StaffRole =
  | "super_admin"
  | "admin"
  | "retailer_onboarding_executive"
  | "retailer_onboarding_executive_walletrequesthandler"
  | "wallet_request_handler";
export type SessionRole = "retailer" | "admin" | StaffRole;

export interface ModulePermission {
  view: boolean;
  create: boolean;
  edit: boolean;
  approve: boolean;
  delete: boolean;
}

export type PermissionMatrix = Record<string, ModulePermission>;

export interface Session {
  id?: string;
  role: SessionRole;
  isStaff?: boolean;
  permissions?: PermissionMatrix | null;
  customPermissions?: PermissionMatrix | null;
  retailerId?: string;
  token?: string;
  email?: string;
  name?: string;
  userCode?: string;
  mobile?: string;
  shopName?: string;
  deviceId?: string;
  kycStatus?: string;
  walletBalance?: number;
  isEmailVerified?: boolean;
  merchantToken?: string | null;
}

export function isStaffRole(role?: string): boolean {
  if (!role) return false;
  const r = role.toLowerCase().replace(/[\s-]+/g, "_");
  return [
    "super_admin",
    "admin",
    "retailer_onboarding_executive",
    "retailer_onboarding_executive_walletrequesthandler",
    "wallet_request_handler",
  ].includes(r);
}

export function isStaffSession(session: Session | null): boolean {
  if (!session) return false;
  return Boolean(session.isStaff || isStaffRole(session.role));
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed) return null;
    if (parsed.role !== "retailer" && !isStaffRole(parsed.role) && parsed.role !== "admin") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(session: Session | null): void {
  if (session) {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    window.dispatchEvent(new CustomEvent("mandatepro_session_updated", { detail: session }));
  } else {
    localStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new CustomEvent("mandatepro_session_updated", { detail: null }));
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new CustomEvent("mandatepro_session_updated", { detail: null }));
}

export function getSelfRetailerId(): string {
  const s = getSession();
  if (s?.role === "retailer" && s.retailerId) return s.retailerId;
  return s?.retailerId || s?.id || "";
}